import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { RenewalContract } from '~/services/modules/renewal';
import RenewalChannelSelector from '../components/RenewalChannelSelector';
import RenewalContractSummary from '../components/RenewalContractSummary';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import { getRenewalChannelOptions, type RenewalChannel } from '../renewalChannel';

type Props = {
  contract: RenewalContract;
  channel: RenewalChannel;
  sending: boolean;
  onChannelChange: (channel: RenewalChannel) => void;
  onSend: () => void;
  onSupport: () => void;
};

const QUESTION_ID = 'renewal-identify-question';

const SEND_LABEL: Record<RenewalChannel, string> = {
  email: 'Enviar código por e-mail',
  sms: 'Enviar código por SMS'
};

/** Etapa 1 da renovação 2026 (Figma 2079:6265): resumo do contrato e escolha do canal do código. */
export default function RenewalIdentifyStep({ contract, channel, sending, onChannelChange, onSend, onSupport }: Props) {
  const options = useMemo(() => getRenewalChannelOptions(contract.insured ?? {}), [contract.insured]);

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.stepIdentify}
      data-testid="renewal-step-identify"
      sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
    >
      <Typography component="h1" sx={{ textAlign: 'center', width: '100%' }} variant="h5">
        Renovação do contrato
      </Typography>

      <RenewalContractSummary appId={RENEWAL_APP_IDS.contractSummary} contract={contract} />

      <Typography id={QUESTION_ID} sx={{ textAlign: 'center', width: '100%' }} variant="subtitle1">
        Como você deseja receber seu código de verificação?
      </Typography>

      <RenewalChannelSelector
        appId={RENEWAL_APP_IDS.channelSelector}
        disabled={sending}
        labelledBy={QUESTION_ID}
        onChange={onChannelChange}
        options={options}
        value={channel}
      />

      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        <Button
          data-app-id={RENEWAL_APP_IDS.sendCode}
          disabled={sending}
          fullWidth
          onClick={onSend}
          size="large"
          startIcon={sending ? <CircularProgress aria-hidden color="inherit" size={18} /> : undefined}
          variant="contained"
        >
          {sending ? 'Enviando código' : SEND_LABEL[channel]}
        </Button>

        <Box
          sx={{
            alignItems: 'center',
            columnGap: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            Não reconhece este e-mail ou telefone?
          </Typography>
          <Button
            data-app-id={RENEWAL_APP_IDS.identifySupport}
            onClick={onSupport}
            sx={(theme) => ({ color: theme.customColors.tertiaryMain })}
            variant="text"
          >
            Fale com o atendimento
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
